import { useCallback, useEffect, useRef, useState } from 'react';
import { stateSetter } from '@/utils';
import { IServerColumnConfig } from '@/components/table/column-config';
import {
	deleteType,
	fetchAnnouncementTypesTableConfig,
	fetchIconOptions,
	fetchTypes,
	postType,
	putType
} from './actions';
import {
	ANNOUNCEMENT_ICON_OPTIONS,
	AnnouncementTypesReturnType,
	IAnnouncementType,
	IAnnouncementTypePayload
} from './types';

export function useAnnouncementTypes(
	getOnRender: boolean | undefined = undefined,
	queryParam: string | undefined = undefined,
	itemsSet = 10,
	withTableConfig = false
): AnnouncementTypesReturnType {
	const [data, setData] = useState<IAnnouncementType[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
	const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
	const [enableQuery, setEnableQuery] = useState<boolean>(false);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(itemsSet);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(0);
	const [iconOptions, setIconOptions] = useState(ANNOUNCEMENT_ICON_OPTIONS);
	// Configurația de coloane (api/announcement-types/table-config) — cerută doar de
	// pagina de listă (withTableConfig); la eroare rămâne null și tabelul cade silențios
	// pe coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	// Gardă anti-race pe listă: la schimbări rapide de query (căutare BE cu debounce)
	// pot fi în zbor mai multe cereri, iar răspunsurile pot sosi în altă ordine —
	// se aplică DOAR ultima cerută.
	const listRequestSeq = useRef(0);

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchAnnouncementTypesTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	const getTypes = useCallback(async (pageParam: number, itemsParam: number) => {
		listRequestSeq.current += 1;
		const seq = listRequestSeq.current;
		const isCurrent = (): boolean => listRequestSeq.current === seq;
		try {
			setLoading(true);
			const response = await fetchTypes('', pageParam, itemsParam, (tp, tc) => {
				if (isCurrent()) handleResponse(tp, tc);
			});
			if (isCurrent()) setData(response);
		} catch {
			// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
		} finally {
			if (isCurrent()) setLoading(false);
		}
	}, []);

	const getTypesQuery = useCallback(
		async (query: string, pageParam: number, itemsParam: number) => {
			listRequestSeq.current += 1;
			const seq = listRequestSeq.current;
			const isCurrent = (): boolean => listRequestSeq.current === seq;
			try {
				if (!enableQuery) setEnableQuery(true);
				setLoading(true);
				const response = await fetchTypes(query, pageParam, itemsParam, (tp, tc) => {
					if (isCurrent()) handleResponse(tp, tc);
				});
				if (isCurrent()) setData(response);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				if (isCurrent()) setLoading(false);
			}
		},
		[enableQuery]
	);

	const createType = useCallback(
		async (payload: IAnnouncementTypePayload, onSuccess?: () => void) => {
			try {
				setLoadingCreate(true);
				const response = await postType(payload);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCreate(false);
			}
		},
		[]
	);

	const editType = useCallback(
		async (typeId: number | string, payload: IAnnouncementTypePayload, onSuccess?: () => void) => {
			try {
				setLoadingUpdate(true);
				const response = await putType(typeId, payload);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	const removeType = useCallback(
		async (typeId: number | string, onSuccess?: () => void) => {
			try {
				setLoadingDelete(true);
				const response = await deleteType(typeId);
				if (typeof response === 'number' && response >= 200 && response < 300) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingDelete(false);
			}
		},
		[]
	);

	const getIconOptions = useCallback(async () => {
		const options = await fetchIconOptions();
		if (options.length) setIconOptions(options);
	}, []);

	useEffect(() => {
		if (getOnRender) {
			getIconOptions();
		}

	}, [getOnRender, getIconOptions]);

	useEffect(() => {
		if (getOnRender && queryParam === undefined) {
			getTypes(page, items);
		}

	}, [getOnRender, page, items]);

	useEffect(() => {
		if (queryParam !== undefined) {
			getTypesQuery(queryParam, page, items);
		} else if (enableQuery) {
			getTypes(page, items);
		}

	}, [queryParam, page, items]);

	return {
		data,
		setData: setData as stateSetter<IAnnouncementType[]>,
		loading,
		tableConfig,
		loadingTableConfig,
		loadingCreate,
		loadingUpdate,
		loadingDelete,
		iconOptions,
		getIconOptions,
		getTypes,
		getTypesQuery,
		createType,
		editType,
		removeType,
		page,
		items,
		setPage,
		setItems,
		totalPages,
		totalItems
	};
}
